import type { SizeSelect } from '@urbanmetrix/sdk';
import type { GeodataNode, GridNode, ID, ProjectNode, SimulationNode } from 'types/project';
import type { GeodataYamlKind } from 'api/geodataApi';

export type TreeNode = GridNode | GeodataNode | SimulationNode;
export type TreeData = { nodes: GridNode[] };
export type RunStateMap = Record<ID, boolean>;
export type EntitySizeMap = {
  grids: Record<string, number>;
  geodatas: Record<string, number>;
  simulations: Record<string, number>;
  subSimulations: Record<string, number>;
};

export const EMPTY_ENTITY_SIZE_MAP: EntitySizeMap = {
  grids: {},
  geodatas: {},
  simulations: {},
  subSimulations: {},
};
export const CLOSED_NODES_STORAGE_PREFIX = 'project-table-closed-nodes:';
export const UPGRADE_PROJECT_CONFIRMATION_TEXT =
  'When upgrading a project, all of its geodata and simulation counters will be reset. Additionally, if the project was originally created under a lower-tier plan, the volume limit will be adjusted to match the current plan. To perform this upgrade, one project credit will be used.';
export const GEODATA_YAML_OPTIONS: Array<{ kind: GeodataYamlKind; label: string }> = [
  { kind: 'grid', label: 'Grid' },
  { kind: 'roads', label: 'Roads' },
  { kind: 'ground', label: 'Ground' },
  { kind: 'buildings', label: 'Buildings' },
  { kind: 'vegetation', label: 'Vegetation' },
  { kind: 'targets', label: 'Targets' },
];
export const SUPPORT_FLAG_REQUIRED_MESSAGE = 'Support access must be enabled for this project before adding resources.';

export const getStoredClosedNodes = (projectId?: string): ID[] => {
  if (!projectId) return [];
  try {
    const parsedValue = JSON.parse(localStorage.getItem(`${CLOSED_NODES_STORAGE_PREFIX}${projectId}`) ?? '[]');
    return Array.isArray(parsedValue)
      ? parsedValue.filter((id): id is ID => typeof id === 'string' || typeof id === 'number')
      : [];
  } catch {
    return [];
  }
};

type TreeNodeWithChildren = { id: ID; nodes?: TreeNodeWithChildren[] | null };

export const getTreeNodeIds = (nodes: TreeNodeWithChildren[]): ID[] =>
  nodes.flatMap((node) => [node.id, ...getTreeNodeIds(node.nodes ?? [])]);

export const filterDeletedProjectItems = (project: ProjectNode, showDeletedItems: boolean): ProjectNode =>
  showDeletedItems
    ? project
    : {
        ...project,
        grids: project.grids
          .filter((grid) => !grid.deletedAt)
          .map((grid) => ({
            ...grid,
            geodatas: grid.geodatas
              .filter((geodata) => !geodata.deletedAt)
              .map((geodata) => ({
                ...geodata,
                simulations: geodata.simulations
                  .filter((simulation) => !simulation.deletedAt)
                  .map((simulation) => ({
                    ...simulation,
                    subSimulations: simulation.subSimulations.filter((subSimulation) => !subSimulation.deletedAt),
                  })),
              })),
          })),
      };

const sumSizeValues = (values: ReadonlyArray<number | null | undefined>): number =>
  values.reduce<number>((total, value) => total + (Number.isFinite(value) ? Number(value) : 0), 0);

export const mapGridSizeResultsToEntitySizes = (sizeResults: SizeSelect[]): EntitySizeMap =>
  sizeResults.reduce<EntitySizeMap>(
    (acc, sizeResult) => {
      for (const grid of sizeResult?.grids ?? []) {
        acc.grids[String(grid.id)] = Number(grid.size) || 0;
        for (const geodata of grid.geodatas ?? []) {
          acc.geodatas[String(geodata.id)] = sumSizeValues([
            geodata.buildingsSize,
            geodata.roadsSize,
            geodata.vegetationSize,
            geodata.groundSize,
          ]);
          for (const simulationSet of geodata.simulationSets ?? []) {
            let simulationSetTotal = 0;
            for (const simulation of simulationSet.simulations ?? []) {
              const resultSize = sumSizeValues((simulation.Results ?? []).map(({ size }: { size: number }) => size));
              const total = sumSizeValues([
                simulation.RaysSize,
                simulation.IntersectionsSize,
                simulation.MultilinesSize,
                resultSize,
              ]);
              acc.subSimulations[`${String(simulationSet.id)}:${String(simulation.id)}`] = total;
              simulationSetTotal += total;
            }
            acc.simulations[String(simulationSet.id)] = simulationSetTotal;
          }
        }
      }
      return acc;
    },
    { grids: {}, geodatas: {}, simulations: {}, subSimulations: {} }
  );

export const toFiniteNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getDraftLifecycleStatus = (item: TreeNode | null) => {
  if (!item) return null;
  return typeof item.lifecycleStatus === 'string' && item.lifecycleStatus.length > 0
    ? item.lifecycleStatus
    : typeof item.status === 'string'
      ? item.status
      : null;
};

export const getDraftResumeTarget = (item: TreeNode | null) => {
  if (!item || getDraftLifecycleStatus(item) !== 'DRAFT' || item.type !== 'geodata') return null;
  if (!item.projectId || !item.gridId || !item.id) return null;
  const simulationSetId = item.simulations?.[0]?.id ? String(item.simulations[0].id) : null;
  return {
    step: simulationSetId ? ('simulation' as const) : ('grid' as const),
    projectId: String(item.projectId),
    gridId: String(item.gridId),
    geodataId: String(item.id),
    simulationSetId,
  };
};

export const normalizeDateString = (value?: string | Date | null) =>
  value ? (value instanceof Date ? value.toISOString() : value) : undefined;
